import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Input, Select, Tabs, Divider } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { toast } from 'react-toastify';
import { BeatLoader } from "react-spinners";
import { createRadiologyExam, updateRadiologyExam } from '../../../services/radiology_exam';
import './AddRadiologyExamModal.css';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const validationSchema = Yup.object().shape({
  category: Yup.string().required('Category is required'),
  examName: Yup.string().required('Exam name is required'),
  minPrice: Yup.number().nullable().typeError('Price must be a number'),
  maxPrice: Yup.number().nullable().typeError('Price must be a number')
    .test('max-greater-than-min', 'Maximum price must be greater than minimum price', 
      function(value) {
        const { minPrice } = this.parent;
        if (!minPrice || !value) return true;
        return value > minPrice;
      }),
  description: Yup.string()
});

const AddRadiologyExamModal = ({ onClose, onContentAdded, editData = null }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [categories, setCategories] = useState([
    'X-Ray', 'Ultrasound', 'CT Scan', 'MRI', 'Mammography', 
    'Fluoroscopy', 'Nuclear Medicine', 'PET Scan', 'Other'
  ]);
  
  const isEditMode = !!editData;

  const initialValues = isEditMode ? {
    ...editData,
    // Ensure all required fields exist even if not in editData
    categoryFr: editData.categoryFr || '',
    minPrice: editData.minPrice || '',
    maxPrice: editData.maxPrice || '',
    description: editData.description || '',
    descriptionFr: editData.descriptionFr || ''
  } : {
    category: '',
    categoryFr: '',
    examName: '',
    minPrice: '',
    maxPrice: '',
    description: '',
    descriptionFr: ''
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Prepare data for submission
      const examData = {
        ...values,
        // Convert price fields to numbers if they're not empty
        minPrice: values.minPrice ? Number(values.minPrice) : null,
        maxPrice: values.maxPrice ? Number(values.maxPrice) : null
      };

      if (isEditMode) {
        await updateRadiologyExam(editData.id, examData);
        toast.success('Radiology exam updated successfully');
      } else {
        await createRadiologyExam(examData);
        toast.success('Radiology exam created successfully');
      }

      onContentAdded();
    } catch (error) {
      toast.error(isEditMode ? 'Error updating radiology exam' : 'Error creating radiology exam');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="modal-wrapper">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Radiology Exam' : 'Add New Radiology Exam'}</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form className="form-container">
                <Tabs 
                  activeKey={activeTab} 
                  onChange={handleTabChange}
                  className="language-tabs"
                >
                  <TabPane tab="English" key="1">
                    <div className="english-section">
                      <div className="form-group">
                        <label>Category *</label>
                        <Select
                          className="custom-select"
                          onChange={(value) => setFieldValue('category', value)}
                          value={values.category}
                          placeholder="Select category"
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {categories.map((category) => (
                            <Option key={category} value={category}>
                              {category}
                            </Option>
                          ))}
                        </Select>
                        {errors.category && touched.category && (
                          <div className="error-message">{errors.category}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Exam Name *</label>
                        <Input
                          name="examName"
                          onChange={handleChange}
                          value={values.examName}
                          className="custom-input"
                          placeholder="Enter exam name"
                        />
                        {errors.examName && touched.examName && (
                          <div className="error-message">{errors.examName}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Price Range (FCFA)</label>
                        <div className="price-inputs">
                          <div className="price-input">
                            <Input
                              name="minPrice"
                              onChange={handleChange}
                              value={values.minPrice}
                              className="custom-input"
                              placeholder="Minimum price"
                              type="number"
                            />
                            {errors.minPrice && touched.minPrice && (
                              <div className="error-message">{errors.minPrice}</div>
                            )}
                          </div>
                          <div className="price-input">
                            <Input
                              name="maxPrice"
                              onChange={handleChange}
                              value={values.maxPrice}
                              className="custom-input"
                              placeholder="Maximum price"
                              type="number"
                            />
                            {errors.maxPrice && touched.maxPrice && (
                              <div className="error-message">{errors.maxPrice}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Description (Optional)</label>
                        <TextArea
                          name="description"
                          onChange={handleChange}
                          value={values.description}
                          className="custom-textarea"
                          rows={4}
                          placeholder="Additional information about the exam"
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tab="French (Optional)" key="2">
                    <div className="french-section">
                      <div className="form-group">
                        <label>Category (French)</label>
                        <Input
                          name="categoryFr"
                          onChange={handleChange}
                          value={values.categoryFr}
                          className="custom-input"
                          placeholder="French category name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description (French)</label>
                        <TextArea
                          name="descriptionFr"
                          onChange={handleChange}
                          value={values.descriptionFr}
                          className="custom-textarea"
                          rows={4}
                          placeholder="Additional information in French"
                        />
                      </div>
                    </div>
                  </TabPane>
                </Tabs>

                <div className="button-container">
                  {loading ? (
                    <BeatLoader color="#3080ED" />
                  ) : (
                    <button type="submit" className="submit-button">
                      {isEditMode ? 'Update Radiology Exam' : 'Add Radiology Exam'}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default AddRadiologyExamModal;